import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../store';
import { api } from '../../utils/api';
import { isoToDmy } from '../DocStorage/UploadWizard';
import PopupHomeNotice from '../PopupHomeNotice';
import { usePopupHome, HOME_FILING } from '../../services/popupHome';   // Filing, one tab only (report c2846304)

// ── Our Documents: the name questions (Calem, report 10ab8ab1, 3rd opening) ────────────────────────
// "Pop up anything that you believe to be misspelled, no spaces, too many spaces, no company added
// (when you think there should be a company) etc. on my screen for me to check it. Make it editable so
// I can change it and then click submit."
// The server reads the archive's own prevailing spelling of every person and every company and writes
// each question out with the correction already filled in (server/src/services/ourDocsNameChecks.ts).
// This watcher asks them, ONE card at a time: the document, the folder it is filed in, what looks wrong
// about its name, and the corrected name in a box he can change before pressing Submit. Submit renames
// through the library's own door (PATCH /api/files/:id — the door the Duplicates panel's pencil uses)
// and then answers the question, so the name it wrote is not put back to him in turn. Skip puts the
// question away for good: that name is never asked about again unless it changes.
// It follows Drop File's pop-up (DropProposals) in everything else: mounted once in AuthGate, asked for
// by nobody but the people entitled to edit this library, and never opened over somebody mid-sentence.

// The archive's editors — the 'management' role or an administrator, read from /api/me, exactly as the
// Duplicates panel is gated (FileLibrary/index.tsx) and as the server gates both endpoints.
// Each look reads all 143,000 names, so it is a slow heartbeat, and never two looks in quick succession.
const POLL_MS = 600_000;
const MIN_GAP_MS = 120_000;
const FIRST_MS = 6_000;
// Never opens under someone mid-sentence — keyed on keystrokes, as DropProposals / JobStatusConfirm are.
// Once it is open, typing into its own box keeps it open.
const TYPING_IDLE_MS = 4000;

type Reason = { code: string; was: string; now: string; count: number; type: string; person: string };
// `kind` is 'number' for the Title Deed / Identification numbers read off the documents themselves
// (reports dc86595a + 60f4748a), 'name' for a spelling question. One file can carry one of each, so a
// card is held by kind AND id, and Skip/Submit say which question they answered.
// 'rule' (report b5927a27) is a term he changed by renaming a document in Our Documents: `id` is the term,
// `name`/`folder` the document he renamed, the one reason 'rule_ask' the change. Yes makes it the firm's rule
// — the other documents still carrying the old term come back as spelling questions, and the AI names new
// ones that way — and Only this file leaves it at that one document. Nothing is renamed by either answer.
type Row = { id: string; kind?: string; name: string; folder: string; date: string; suggested: string; reasons: Reason[] };
const keyOf = (r: Row) => `${r.kind || 'name'}:${r.id}`;

const inp: React.CSSProperties = { width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid var(--border-strong)', background: 'var(--c-gray-50)', borderRadius: 8, color: 'var(--text-primary)', boxSizing: 'border-box' };
const lbl: React.CSSProperties = { fontSize: 10.5, fontWeight: 700, letterSpacing: '.03em', textTransform: 'uppercase', color: 'var(--text-secondary)', display: 'block', margin: '10px 0 4px' };
const val: React.CSSProperties = { fontSize: 13.5, fontWeight: 600, color: 'var(--text-primary)', overflowWrap: 'anywhere' };

export default function NameChecks() {
  const { t } = useTranslation();
  const me = useStore(s => s.me);
  const showToast = useStore(s => s.showToast);
  const mine = !!me && (me.role === 'admin' || (me.roles || []).includes('management'));

  const [rows, setRows] = useState<Row[]>([]);
  const [names, setNames] = useState<Record<string, string>>({});   // the box, per file
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const [typing, setTyping] = useState(false);
  const lastKey = useRef(0);
  const shown = useRef(false);
  useEffect(() => {
    if (!mine) return;
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      const tag = (el?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || el?.isContentEditable) { lastKey.current = Date.now(); setTyping(true); }
    };
    document.addEventListener('keydown', onKey, true);
    const id = setInterval(() => setTyping(Date.now() - lastKey.current < TYPING_IDLE_MS), 1000);
    return () => { document.removeEventListener('keydown', onKey, true); clearInterval(id); };
  }, [mine]);

  const lastAt = useRef(0);
  const open = useRef(0);      // how many questions are in hand: a fresh look is only ever taken with none
  const poll = useCallback(async (force?: boolean) => {
    if (!localStorage.getItem('mf_token')) return;
    if (open.current) return;         // a card is up: the stack is never shuffled under him mid-answer
    if (!force && Date.now() - lastAt.current < MIN_GAP_MS) return;   // never two scans in quick succession
    lastAt.current = Date.now();
    try {
      const d: any = await api.filesNameChecks();
      setRows(Array.isArray(d?.checks) ? d.checks : []);
    } catch { /* transient — the next look brings them back */ }
  }, []);
  useEffect(() => {
    if (!mine) { setRows([]); return; }
    // A few seconds after the page has settled, and then on the heartbeat: one look reads every name in
    // the archive, so it never competes with the page's own first load.
    const first = setTimeout(() => { void poll(true); }, FIRST_MS);
    const id = setInterval(() => { void poll(true); }, POLL_MS);
    return () => { clearTimeout(first); clearInterval(id); };
  }, [mine, poll]);
  // …and when he comes back to this tab, as long as a scan has not just run.
  useEffect(() => {
    if (!mine) return;
    const onVisible = () => { if (document.visibilityState === 'visible') void poll(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [mine, poll]);

  open.current = rows.length;
  const current = rows[0];
  const curKey = current ? keyOf(current) : '';
  useEffect(() => { setErr(''); }, [curKey]);

  // Report c2846304: a name is corrected on Filing, so the question is asked in the tab showing that page and
  // in no other; with the page open in no tab, the tab in front offers to open it (PopupHomeNotice).
  const pending = !!mine && !!current && !(typing && !shown.current);
  const home = usePopupHome(HOME_FILING, pending);
  if (!mine || !current || (typing && !shown.current)) { shown.current = false; return null; }
  shown.current = true;
  if (home !== 'show') return home === 'notice' ? <PopupHomeNotice page={HOME_FILING} /> : null;

  const name = names[curKey] === undefined ? current.suggested : names[curKey];
  // This one answered: off the card stack. When it was the last of the load there may be more waiting
  // behind it (total), so one fresh scan is asked for rather than waiting out the heartbeat.
  const done = (key: string) => {
    const left = rows.filter(r => keyOf(r) !== key);
    open.current = left.length;   // …so the look below is allowed, and only when the stack has run out
    setRows(left);
    setNames(ns => { const next = { ...ns }; delete next[key]; return next; });
    setErr('');
    if (!left.length) void poll(true);
  };

  // Submit: rename, then answer. The rename is the library's own PATCH, so the server weighs it exactly
  // as it does from a row of the archive (it refuses a naming example and a name already used in that
  // folder, and re-runs Our Documents' Inactive protocol on the new name). Answering afterwards is what
  // stops the name he has just written coming back as the next question.
  const submit = async () => {
    const wanted = name.trim();
    if (busy || !wanted) return;
    setBusy(true); setErr('');
    try {
      if (wanted !== current.name) {
        await api.filesRename(current.id, wanted, current.suggested);
        showToast(t('nck_renamed', { name: wanted }));
        window.dispatchEvent(new Event('slf:files-changed'));
      }
      try { await api.filesNameCheckSkip(current.id, current.kind); } catch { /* the name is already corrected; the question keeps */ }
      done(curKey);
    } catch (e: any) {
      // Gone already — renamed or trashed by hand, or answered in another tab.
      if (e?.status === 404) { showToast(e.message); done(curKey); }
      else setErr(e?.message || 'Failed');
    } finally { setBusy(false); }
  };
  // Skip: the name stays exactly as it is and this question is never asked again.
  const skip = async () => {
    if (busy) return;
    setBusy(true);
    try { await api.filesNameCheckSkip(current.id, current.kind); } catch { /* still open → the next look brings it back */ }
    done(curKey);
    setBusy(false);
  };
  // A rename's term (kind 'rule'): Yes — from now on — or Only this file. Answered through the same door as
  // Skip, so it comes off the same allowance.
  const answerRule = async (use: boolean) => {
    if (busy) return;
    setBusy(true);
    try { await api.filesNameCheckSkip(current.id, 'rule', use); } catch { /* still open → the next look brings it back */ }
    done(curKey);
    setBusy(false);
  };

  const btn: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, padding: '9px 16px', borderRadius: 9, fontWeight: 600, fontSize: 13, cursor: busy ? 'not-allowed' : 'pointer' };
  const ready = !!name.trim();
  if (current.kind === 'rule') {
    const change = current.reasons[0] || { code: 'rule_ask', was: '', now: current.suggested, count: 0, type: '', person: '' };
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 5380, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
        <div role="dialog" aria-modal="true" style={{ background: 'var(--bg-surface)', borderRadius: 14, width: 560, maxWidth: '96vw', maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: 'var(--shadow-md)' }}>
          <div style={{ padding: '16px 22px 12px', borderBottom: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
              <i className="ti ti-bulb" style={{ fontSize: 22, color: 'var(--accent)' }} aria-hidden />
              <div style={{ flex: 1, minWidth: 0, fontSize: 15, fontWeight: 800, color: 'var(--text-primary)' }}>{t('nck_title_rule')}</div>
              {rows.length > 1 && <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-tertiary)', background: 'var(--c-gray-100)', borderRadius: 999, padding: '2px 9px', whiteSpace: 'nowrap' }}>1 / {rows.length}</span>}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 6, lineHeight: 1.5 }}>{t('nck_hint_rule')}</div>
          </div>

          <div style={{ padding: '14px 22px', minHeight: 0, flex: 1, overflow: 'auto' }}>
            <div style={lbl}>{t('nck_rule_change')}</div>
            <div style={{ padding: '8px 10px', borderRadius: 8, background: '#FFFBEB', border: '1px solid #FDE68A', fontSize: 12.5, color: '#92400E', lineHeight: 1.6, overflowWrap: 'anywhere' }}>
              <div style={{ display: 'flex', gap: 6 }}>
                <i className="ti ti-pencil" style={{ flexShrink: 0, marginTop: 3 }} aria-hidden />
                <span>{t('nck_reason_rule_ask', { was: change.was, now: change.now })}</span>
              </div>
              {change.count > 0 && <div style={{ marginTop: 4, color: 'var(--text-secondary)' }}>{t('nck_rule_others', { n: change.count, was: change.was })}</div>}
            </div>
            {current.name && <>
              <div style={lbl}>{t('nck_filed_as')}</div>
              <div style={val}>{current.name}</div>
            </>}
            {current.folder && <>
              <div style={lbl}>{t('nck_folder')}</div>
              <div style={{ ...val, fontWeight: 400, fontSize: 12.5, color: 'var(--text-secondary)' }}>
                <i className="ti ti-folder" style={{ marginRight: 5, color: 'var(--accent)' }} aria-hidden />{current.folder}
              </div>
            </>}
          </div>

          <div style={{ display: 'flex', gap: 8, padding: '12px 22px 18px', borderTop: '1px solid var(--border)' }}>
            <button onClick={() => answerRule(true)} disabled={busy} title={t('nck_rule_yes_hint')}
              style={{ ...btn, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 700, opacity: busy ? 0.7 : 1 }}>
              <i className={busy ? 'ti ti-loader-2' : 'ti ti-check'} style={{ fontSize: 14 }} aria-hidden />{t('nck_rule_yes')}
            </button>
            <button onClick={() => answerRule(false)} disabled={busy} title={t('nck_rule_only_hint')} style={{ ...btn, border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-primary)' }}>
              <i className="ti ti-file" style={{ fontSize: 14 }} aria-hidden />{t('nck_rule_only')}
            </button>
          </div>
        </div>
      </div>
    );
  }
  return (
    // Under Drop File's own pop-up and the job pop-ups: a document waiting to be filed, or a status
    // email waiting on its confirmation, is the more urgent of the two.
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 5380, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div role="dialog" aria-modal="true" style={{ background: 'var(--bg-surface)', borderRadius: 14, width: 560, maxWidth: '96vw', maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: 'var(--shadow-md)' }}>
        <div style={{ padding: '16px 22px 12px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <i className="ti ti-help-circle" style={{ fontSize: 22, color: 'var(--accent)' }} aria-hidden />
            <div style={{ flex: 1, minWidth: 0, fontSize: 15, fontWeight: 800, color: 'var(--text-primary)' }}>{t(current.kind === 'number' ? 'nck_title_number' : 'nck_title')}</div>
            {rows.length > 1 && <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-tertiary)', background: 'var(--c-gray-100)', borderRadius: 999, padding: '2px 9px', whiteSpace: 'nowrap' }}>1 / {rows.length}</span>}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 6, lineHeight: 1.5 }}>{t(current.kind === 'number' ? 'nck_hint_number' : 'nck_hint')}</div>
        </div>

        <div style={{ padding: '14px 22px', minHeight: 0, flex: 1, overflow: 'auto' }}>
          <div style={lbl}>{t('nck_filed_as')}</div>
          <div style={val}>{current.name}</div>
          <div style={lbl}>{t('nck_folder')}</div>
          <div style={{ ...val, fontWeight: 400, fontSize: 12.5, color: 'var(--text-secondary)' }}>
            <i className="ti ti-folder" style={{ marginRight: 5, color: 'var(--accent)' }} aria-hidden />{current.folder}
          </div>
          <div style={lbl}>{t('nck_date')}</div>
          <div style={{ ...val, fontWeight: 400, fontSize: 12.5, color: 'var(--text-secondary)' }}>{isoToDmy(current.date)}</div>

          <div style={lbl}>{t(current.kind === 'number' ? 'nck_missing' : 'nck_wrong')}</div>
          <div style={{ padding: '8px 10px', borderRadius: 8, background: '#FFFBEB', border: '1px solid #FDE68A', fontSize: 12, color: '#92400E', lineHeight: 1.6 }}>
            {current.reasons.map((r, i) => (
              <div key={i} style={{ display: 'flex', gap: 6, overflowWrap: 'anywhere' }}>
                <i className="ti ti-alert-triangle" style={{ flexShrink: 0, marginTop: 2 }} aria-hidden />
                <span>{t(`nck_reason_${r.code}`, { was: r.was, now: r.now, n: r.count, type: r.type, person: r.person })}</span>
              </div>
            ))}
          </div>

          <label style={lbl}>{t('nck_new_name')}</label>
          <input value={name} onChange={e => setNames(ns => ({ ...ns, [curKey]: e.target.value }))}
            onKeyDown={e => { if (e.key === 'Enter') void submit(); }} style={{ ...inp, fontWeight: 600 }} />
          {name.trim() === current.name && <div style={{ marginTop: 3, fontSize: 11, color: 'var(--text-tertiary)' }}>{t('nck_unchanged')}</div>}
          {err && <div style={{ marginTop: 8, fontSize: 12, color: '#E24B4A', overflowWrap: 'anywhere' }}>{err}</div>}
        </div>

        <div style={{ display: 'flex', gap: 8, padding: '12px 22px 18px', borderTop: '1px solid var(--border)' }}>
          <button onClick={submit} disabled={busy || !ready}
            style={{ ...btn, border: 'none', background: ready ? 'var(--accent)' : 'var(--c-gray-100)', color: ready ? '#fff' : 'var(--text-tertiary)', fontWeight: 700, opacity: busy ? 0.7 : 1, cursor: busy || !ready ? 'not-allowed' : 'pointer' }}>
            <i className={busy ? 'ti ti-loader-2' : 'ti ti-check'} style={{ fontSize: 14 }} aria-hidden />{t('nck_submit')}
          </button>
          <button onClick={skip} disabled={busy} title={t('nck_skip_hint')} style={{ ...btn, border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-primary)' }}>
            <i className="ti ti-player-skip-forward" style={{ fontSize: 14 }} aria-hidden />{t('nck_skip')}
          </button>
        </div>
      </div>
    </div>
  );
}
